import Button from "components/Button/Button";
import Dropdown from "components/Dropdown/Dropdown";
import InputField from "components/InputField/InputField";
import Modal from "components/Modal/Modal";
import { CreateUserInput, PermissionArrayItem } from "TypesAndInterfaces";
import "./CreateUserForm.scss";
import eye from "../../assets/images/icons/eye.svg";
import { createUserMutation, updateUserMutation } from "pages/Users/mutations";
import { useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useState } from "react";
import { permissionsArray } from "../../data";
import { useEffect } from "react";
import { getUserQuery } from "pages/Users/queries";
import InputEmailField from "components/InputEmailField/InputEmailField";

interface IProps {
  initialUserState: CreateUserInput;
  isCreateUserModalOpened: boolean;
  userId: number | undefined;
  fetchUsers: Function;
  setIsCreateUserModalOpened: Function;
  setUserId: Function;
  marketplaceId?: number;
}

const CreateUserForm = ({
  initialUserState,
  isCreateUserModalOpened,
  userId,
  fetchUsers,
  setIsCreateUserModalOpened,
  setUserId,
  marketplaceId,
}: IProps) => {
  //#region hooks
  const [createdUser, setCreatedUser] =
    useState<CreateUserInput>(initialUserState);
  const [permissions, setPermissions] = useState<PermissionArrayItem[]>([]);
  const [confirmPasswordField, setConfirmPasswordField] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [saveButtonText, setSaveButtonText] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [emailList, setEmailList] = useState<string>("");
  //#endregion

  //#region API CALLS
  const [getUser] = useLazyQuery(getUserQuery, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (dataUser) => {
      setCreatedUser({
        email: dataUser.getUser.email,
        disabled: dataUser.getUser.disabled,
        name: dataUser.getUser.name,
        password: dataUser.getUser.password,
        permissions: [],
      });
      setPermissions(
        dataUser.getUser?.permissions?.map(
          (item: any) => ({ label: item, value: item } as PermissionArrayItem)
        )
      );
      setConfirmPasswordField(dataUser.getUser.password);
    },
  });

  const [createUser, { loading }] = useMutation(createUserMutation, {
    onCompleted: () => {
      toast.info(`User created.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      fetchUsers();
      setIsCreateUserModalOpened(false);
      resetCreateUserData();
      setButtonDisabled(false);
      setEmailList("");
    },
    onError: () => {
      toast.error(`error! submitted data:, ${createdUser.email},
        ${createdUser.disabled},
        ${createdUser.name},
        ${createdUser.password},
      `);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });

  const [updateUser] = useMutation(updateUserMutation, {
    onCompleted: () => {
      toast.info(`User updated.`, {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      fetchUsers();
      setIsCreateUserModalOpened(false);
      resetCreateUserData();
      setButtonDisabled(false);

    },
    onError: () => {
      toast.error(`error! submitted data:, ${createdUser.email},
          ${createdUser.disabled},
          ${createdUser.name},
          ${createdUser.password},
        `);
      setTimeout(() => {
        setButtonDisabled(false);
      }, 1500);
    },
  });
  //#endregion

  const resetCreateUserData = () => {
    setCreatedUser({
      email: "",
      disabled: false,
      name: "",
      password: "",
      permissions: [],
    });
    setPermissions([]);
    setConfirmPasswordField("");
    setUserId(undefined);
    setEmailList("");
  };

  const handleIsCreateUserModalOpened = (isOpened: boolean) => {
    setIsCreateUserModalOpened(isOpened);
    if (!isOpened) {
      resetCreateUserData();
    }
  };

  const handleCreateUser = async () => {
    if (userId) {
      await updateUser({
        variables: {
          data: {
            id: userId,
            email: createdUser.email,
            disabled: createdUser.disabled,
            name: createdUser.name,
            password: createdUser.password,
            permissions: permissions.map((item) => item.value),
          },
        },
      });
      fetchUsers();
      setEmailList("");
    } else {
      if (createdUser.password === confirmPasswordField) {
        setPermissions(
          permissions.map((item: PermissionArrayItem) => {
            return {
              key: item.key,
              label: item.label,
              value: item.value,
            };
          })
        );
        if (createdUser.email && createdUser.name && createdUser.password) {
          await createUser({
            variables: {
              data: {
                email: createdUser.email,
                disabled: createdUser.disabled,
                name: createdUser.name,
                password: createdUser.password,
                permissions: permissions.map(
                  (p: PermissionArrayItem) => p.value
                ),
                marketplaceId,
              },
            },
          });
          fetchUsers();
          setEmailList("")
        }
      }
    }
  };

  useEffect(() => {
    if (userId) {
      getUser({ variables: { userId } });
      setPageTitle("Edit User");
      setSaveButtonText("Edit");
    } else {
      setPageTitle("Create a New User");
      setSaveButtonText("Create");
    }
  }, [getUser, userId]);

  useEffect(() => {
    setEmailList(createdUser.email.replace(/"/g, ''));
  }, [createdUser.email]);

  useEffect(() => {
    setCreatedUser((prev: CreateUserInput) => ({
      ...prev,
      email: emailList,
    }))
  }, [emailList])

  const validateEmails = (input: string) => {
    const regex = /^[\w-\\.\\+]+@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emails = input.replace(/"/g, '').split(',');
    setButtonDisabled(!emails.every(email => regex.test(email.trim())));
  }

  return (
    <Modal
      isOpened={isCreateUserModalOpened}
      className="user-form"
      title={pageTitle}
      setIsOpenedCallback={handleIsCreateUserModalOpened}
    >
      <form>
        <InputField
          label={"User Name"}
          type="text"
          placeholder={"Enter the user name"}
          onChange={(e) =>
            setCreatedUser((prev: CreateUserInput) => ({
              ...prev,
              name: e.target.value,
            }))
          }
          value={createdUser.name}
        />
        <Dropdown
          options={permissionsArray}
          placeholder={"Select a role"}
          label={"Permissions"}
          value={permissions}
          isMulti
          onChange={(e: any) => setPermissions(e)}
        />
        <InputEmailField
          key={isCreateUserModalOpened ? 'open' : 'closed'}
          label={"User Email"}
          type="email"
          placeholder="Enter users emails separated by comma or press enter"
          onChange={(e) => {
            validateEmails(e.target.value);
          }}
          defaultValue={emailList}
          setEmails={setEmailList} />
        <InputField
          label={"Password"}
          type={"password"}
          placeholder={"Enter the user password"}
          icon={eye}
          onChange={(e) =>
            setCreatedUser((prev: CreateUserInput) => ({
              ...prev,
              password: e.target.value,
            }))
          }
          value={createdUser.password}
          autoComplete="new-password"
        />
        <InputField
          label={"Confirm Password"}
          type={"password"}
          placeholder={"Enter the user password again"}
          icon={eye}
          className={
            createdUser.password !== confirmPasswordField ? "invalid-input" : ""
          }
          onChange={(e) => setConfirmPasswordField(e.target.value)}
          value={confirmPasswordField}
          autoComplete="new-password"
          errors={
            createdUser.password !== confirmPasswordField
              ? ["Passwords do not match"]
              : []
          }
        />
      </form>
      <div className="modal-buttons-container">
        <Button
          className="button--bordered button--error"
          type="button"
          content="Cancel"
          onClick={() => {
            handleIsCreateUserModalOpened(false)
            setEmailList("")
          }}
        ></Button>
        {loading ? (
          <Button
            className="button--primary"
            type="button"
            icon="sync"
            content={"Loading..."}
            disabled={true}
          ></Button>
        ) : (<Button
          className="button--primary"
          type="button"
          content={saveButtonText}
          onClick={handleCreateUser}
          disabled={
            createdUser.email === "" ||
            createdUser.name === "" ||
            createdUser.password === "" ||
            confirmPasswordField === "" ||
            createdUser.password !== confirmPasswordField ||
            buttonDisabled
          }
        ></Button>)}
      </div>
    </Modal>
  );
};

export default CreateUserForm;
